import thunk from 'redux-thunk';
import serverVars from 'server-vars';
import { DESKTOP, MOBILE, TABLET } from 'dibs-buyer-layout/exports/deviceTypeEnums';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants';

/* Reducers */
import { defaultReducers } from '../../reducers/reducerUtils';
import { pdpRespMakeOfferModalReducer as makeOfferModal } from '../../reducers/pdpRespMakeOfferModalReducer';
import { pdpRespNotificationsReducer as sharedNotificationsReducer } from '../../reducers/pdpRespNotificationsReducer';

/* Components */
import { RelayEnvironmentProvider } from 'react-relay';
import { SocketContextProvider } from 'dibs-subscriptions-utils/exports/SocketContext';
import { ServerVarsContextProvider } from '../../global/ServerVarsContext/ServerVarsContext';
import { PdpRespMarketplaceLayout } from '../../pdp/respMarketplace/PdpRespMarketplaceLayout/PdpRespMarketplaceLayout';

/* Helpers */
import { hydrateRoot } from 'react-dom/client';
import { initializeDBL } from 'dibs-buyer-layout/src/entries';
import { getItemIdFromUri } from '../../utils/getItemIdFormUri';
import { clientRelayEnvironment } from '../../clientRelayEnvironment';
import { setDatadogABTestValues } from 'dibs-ab-tests/exports/clientAbTestV2';
import { pdpRespPrintReducer as pdpPrint } from '../../reducers/pdpRespPrintReducer';
import { datadog, initSharedDatadogRum, pdpTypes } from 'dibs-datadog/exports/datadog';
import { perfMeasureFromPageInit } from 'dibs-tracking/src/prometheus/peformanceTracking';
import { storePdpVisit } from '../../pdp/PdpSharedNotifications/pdpSharedNotificationHelpers';
import { getIsBotOnClient } from 'dibs-buyer-layout/exports/botHelpers';

/* Actions */
import { setRelayVariables } from '../../actions/sharedRelayVariableActions';

/* Notifications */
import { pdpRespNotifications } from '../../pdp/PdpRespNotifications/pdpRespNotifications';

import { initializePrerenderedCheckout } from 'dibs-checkout/exports/prerender/initializePrerenderedCheckout';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { sharedLoginCheckSetUp } from '../../authentication/sharedLoginCheck';
import { setIntlVariables } from '../../actions/sharedIntlVariablesActions';
import { setUserTypeTrade } from '../../actions/sharedMobileActions';

const isPdpRespPreviewPage = serverVars.get('isPdpRespPreviewPage');

/**
 * DBL has a different environment that's created in initializeDBL.
 */
const options = {
    autoRunNotificationQueue: !isPdpRespPreviewPage,
    middleware: [thunk],
    notifications: pdpRespNotifications,
    reducers: {
        pdpPrint,
        sharedNotificationsReducer,
        makeOfferModal,
        ...defaultReducers,
    },
};

const itemId = getItemIdFromUri(location.pathname);
if (itemId) {
    storePdpVisit({ itemId });
}

datadog.load().then(dd => {
    initSharedDatadogRum(dd, {
        device: 'resp',
        page: PAGE_TYPE.PDP,
        pageSubType: pdpTypes.marketplace,
    });
    setDatadogABTestValues();
});

initializeDBL(options).then(async ({ store }) => {
    perfMeasureFromPageInit({ subGroup: 'resp/init-dbl', monitoringGroup: 'unknown' });

    const { environment, socketListeners } = clientRelayEnvironment(
        serverVars.get('dbl.relayData')
    );
    sharedLoginCheckSetUp({ store, environment });

    const locale = serverVars.get('locale');
    const messages = serverVars.get('messages');
    const deviceType = serverVars.get('deviceType');
    const primaryRelayVariables = serverVars.get('relay.primaryRelayVariables');

    store.dispatch(setRelayVariables(primaryRelayVariables));
    store.dispatch(setIntlVariables(serverVars.get('intlVariables')));
    store.dispatch(setUserTypeTrade(serverVars.get('userType') === CUSTOMER_TYPE_TRADE));

    hydrateRoot(
        document.getElementById('js-root') as HTMLElement,
        <RelayEnvironmentProvider environment={environment}>
            <ServerVarsContextProvider
                isBot={getIsBotOnClient()}
                isMobile={deviceType === MOBILE}
                isTablet={deviceType === TABLET}
                isDesktop={deviceType === DESKTOP}
                isPdpRespPreviewPage={isPdpRespPreviewPage}
                areDisplayAdsEnabled={serverVars.get('areDisplayAdsEnabled')}
                isBannerAdsAbTestVariant={serverVars.get('isBannerAdsAbTestVariant')}
                pdpTopYmalSponsoredTestVariant={serverVars.get('pdpTopYmalSponsoredTestVariant')}
                isPdpSellerReviewsVariant={serverVars.get('isPdpSellerReviewsVariant')}
                isPdpEstimateSimilarSoldTestVariant={serverVars.get(
                    'isPdpEstimateSimilarSoldTestVariant'
                )}
                isPersonalizedCollectionTestVariant={serverVars.get(
                    'isPersonalizedCollectionTestVariant'
                )}
                isItemFavoritingVariant={serverVars.get('isItemFavoritingVariant')}
            >
                <SocketContextProvider socketListeners={socketListeners}>
                    <PdpRespMarketplaceLayout
                        store={store}
                        locale={locale}
                        messages={messages}
                        primaryRelayVariables={primaryRelayVariables}
                        secondaryRelayVariables={serverVars.get('relay.secondaryRelayVariables')}
                    />
                </SocketContextProvider>
            </ServerVarsContextProvider>
        </RelayEnvironmentProvider>
    );

    if (itemId) {
        await initializePrerenderedCheckout({
            isMobile: false,
            itemId,
            locale,
            messages,
        });
    }
});
